import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, HelpCircle, User, LogOut } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getUserGeneratedImagesCount, getUserSubscription } from '../services/api';

const Settings = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { logout, user } = useAuth();
  const [generatedCount, setGeneratedCount] = React.useState<number>(0);
  const [currentPlan, setCurrentPlan] = React.useState<string>('Free');

  React.useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id) return;

      try {
        const [imagesCount, subscription] = await Promise.all([
          getUserGeneratedImagesCount(user.id),
          getUserSubscription(user.id)
        ]);

        setGeneratedCount(imagesCount.generatedImagesCount);
        setCurrentPlan(subscription.currentPlan);
      } catch {
        setGeneratedCount(0);
        setCurrentPlan('Free');
      }
    };

    fetchUserData();
  }, [user?.id]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      icon: User,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      icon: isDark ? Sun : Moon,
      label: isDark ? 'Light Mode' : 'Dark Mode',
      onClick: toggleTheme,
    },
    {
      icon: HelpCircle,
      label: 'Help Center',
      onClick: () => navigate('/help'),
    },
    {
      icon: LogOut,
      label: 'Logout',
      onClick: handleLogout,
      danger: true,
    },
  ];

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="mb-8">
        <h1 className={`text-2xl font-light tracking-widest mb-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
          SETTINGS
        </h1>
        <p className="text-sm text-zinc-400 tracking-wider">Customize your experience</p>
      </div>

      {/* Plan Info - Only visible on mobile */}
      <div className="md:hidden mb-6">
        <div className={`p-4 rounded-lg ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} border transition-colors duration-200`}>
          <p className={`text-sm mb-2 font-inter ${isDark ? 'text-white' : 'text-zinc-900'}`}>
            {currentPlan === 'Free' 
              ? `Visual generated : ${generatedCount}/3`
              : `Visual generated : ${generatedCount}`
            }
          </p>
          {currentPlan === 'Free' && (
            <>
              <p className="text-xs text-yellow-500 mb-3 font-inter">
                Upgrade to Basic plan and unlock all features
              </p>
              <button
                onClick={() => navigate('/pricing?upgrade=true')}
                className={`${isDark ? 'bg-white text-zinc-900 hover:bg-zinc-100' : 'bg-zinc-900 text-white hover:bg-zinc-800'} px-4 py-2 rounded-lg text-sm font-inter w-full transition-colors`}
              >
                Upgrade to Basic
              </button>
            </>
          )}
        </div>
      </div>

      <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-zinc-900/30' : 'bg-white'}`}>
        {menuItems.map((item, index) => (
          <button
            key={item.label}
            onClick={item.onClick}
            className={`w-full flex items-center gap-4 p-4 transition-colors ${
              isDark 
                ? item.danger ? 'text-red-500 hover:bg-zinc-800/50' : 'text-white hover:bg-zinc-800/50'
                : item.danger ? 'text-red-500 hover:bg-zinc-100' : 'text-zinc-900 hover:bg-zinc-100'
            } ${index !== menuItems.length - 1 ? `border-b ${isDark ? 'border-zinc-800' : 'border-zinc-200'}` : ''}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default Settings;